import React, {
  type FC,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import classnames from 'classnames';
import { Trans } from 'react-i18next';
import { Slide, Slider } from 'pure-react-carousel';

import { useRWD } from 'hooks';
import { useGetProductsBoughtByOthers, useGetProductsBoughtByOthersMutation } from 'api';

import WiseCarousel from 'pages/wise/WiseProduct/components/WiseCarousel';
import { WiseProductItem } from 'components/containers';

import styles from 'theme/pages/wise/WiseProduct/components/WiseOthersAlsoBought/WiseOthersAlsoBought.module.scss';

const WISE_PRODUCT_CARD_WIDTH_PX = 260;
const CAROUSEL_MAX_VISIBLE_SLIDES = 5;
const SLIDE_GAP_PX = 8;
const WISE_PRODUCT_CARD_HEIGHT_PX = 465;
const INLINE_ROW_HORIZONTAL_PADDING_PX = 48;
const INLINE_ROW_GAP_PX = 42;

function countCarouselCardsThatFitRow(containerWidthPx: number): number {
  if (containerWidthPx <= 0) {
    return 1;
  }
  const slot = WISE_PRODUCT_CARD_WIDTH_PX + SLIDE_GAP_PX;
  return Math.max(1, Math.floor((containerWidthPx + SLIDE_GAP_PX) / slot));
}

function countInlineCardsThatFitRow(containerWidthPx: number): number {
  const inner = Math.max(0, containerWidthPx - INLINE_ROW_HORIZONTAL_PADDING_PX);
  const unit = WISE_PRODUCT_CARD_WIDTH_PX + INLINE_ROW_GAP_PX;
  return Math.max(1, Math.floor((inner + INLINE_ROW_GAP_PX) / unit));
}

export type WiseOthersAlsoBoughtProps = {
  productId: number;
  showNavArrows?: boolean;
};

const WiseOthersAlsoBought: FC<WiseOthersAlsoBoughtProps> = ({ productId, showNavArrows }) => {
  const { isMobile } = useRWD();
  const rootRef = useRef<HTMLDivElement>(null);
  const [rowFit, setRowFit] = useState<{ carousel: number; inline: number } | null>(null);

  const {
    data: productsBoughtByOthersSimpleData,
    isLoading: isProductsBoughtByOthersLoading,
    isFetching: isProductsBoughtByOthersFetching
  } = useGetProductsBoughtByOthers(
      productId,
      {
        page: 1,
        limit: 999
      },
      {
        onSuccess: (data) => {
          refetchProductsFullData({
            scopes: 'PriceProductData',
            productIds: data.items
              .map((item) => item.id)
              .toString()
              .replaceAll(',', '|'),
            page: 1,
            limit: 999
          });
        }
      }
    );

  const { data: productsBoughtByOthersFullData, mutate: refetchProductsFullData } =
    useGetProductsBoughtByOthersMutation(productId);

  const mergedItems = useMemo(() => {
    return productsBoughtByOthersSimpleData?.items?.map((simpleProduct) => {
      return productsBoughtByOthersFullData
        ? {
            ...simpleProduct,
            ...productsBoughtByOthersFullData?.items.find(
              (fullProduct) => fullProduct.id === simpleProduct.id
            )
          }
        : simpleProduct;
    });
  }, [productsBoughtByOthersSimpleData, productsBoughtByOthersFullData]);

  const itemCount = productsBoughtByOthersSimpleData?.items?.length ?? 0;
  const resolvedTitle = <Trans>Inni kupili także</Trans>;
  const resolvedShowArrows = showNavArrows ?? !isMobile;
  const listBusy = isProductsBoughtByOthersLoading || isProductsBoughtByOthersFetching;
  const loadingEmpty = listBusy && itemCount === 0;

  const skeletonSlideCount = isMobile
    ? 1
    : Math.min(
        CAROUSEL_MAX_VISIBLE_SLIDES,
        Math.max(1, rowFit?.carousel ?? CAROUSEL_MAX_VISIBLE_SLIDES)
      );

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return;
    }

    const measure = () => {
      const w = el.getBoundingClientRect().width;
      setRowFit({
        carousel: countCarouselCardsThatFitRow(w),
        inline: countInlineCardsThatFitRow(w)
      });
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [itemCount]);

  /** Karuzela, gdy nie wszystkie produkty mieszczą się w jednym wierszu trybu inline. */
  const useCarousel =
    rowFit === null
      ? itemCount > CAROUSEL_MAX_VISIBLE_SLIDES
      : itemCount > rowFit.inline;

  const visibleSlidesInCarousel = isMobile
    ? 1
    : Math.min(
        CAROUSEL_MAX_VISIBLE_SLIDES,
        itemCount,
        Math.max(1, rowFit?.carousel ?? CAROUSEL_MAX_VISIBLE_SLIDES)
      );

  const renderProductItems = () =>
    mergedItems?.map((product) => (
      <WiseProductItem key={product.id} product={product} />
    ));

  return (
    <div
      ref={rootRef}
      className={classnames(
        styles.wiseOthersRoot,
        'StylePath-Pages-Wise-WiseProduct-components-WiseOthersAlsoBought'
      )}>
      {(loadingEmpty || (itemCount > 0 && useCarousel)) && (
        <WiseCarousel
          totalSlides={itemCount}
          visibleSlides={loadingEmpty ? skeletonSlideCount : visibleSlidesInCarousel}
          naturalSlideWidth={WISE_PRODUCT_CARD_WIDTH_PX}
          naturalSlideHeight={WISE_PRODUCT_CARD_HEIGHT_PX}
          relaxHeight={loadingEmpty}
          title={resolvedTitle}
          showNavArrows={!loadingEmpty && resolvedShowArrows}
          disableKeyboard
          disableAnimation={loadingEmpty || listBusy}
          isLoading={loadingEmpty}>
          {itemCount > 0 ? (
            <Slider>
              {mergedItems?.map((product, index) => (
                <Slide key={product.id} index={index} className={styles.slide}>
                  <div className={styles.productWrapper}>
                    <WiseProductItem product={product} />
                  </div>
                </Slide>
              ))}
            </Slider>
          ) : null}
        </WiseCarousel>
      )}

      {itemCount > 0 && !useCarousel && (
        <div className={styles.inlineBlock}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>{resolvedTitle}</div>
          </div>
          <div className={styles.inlineRow}>{renderProductItems()}</div>
        </div>
      )}
    </div>
  );
};

export default WiseOthersAlsoBought;
